import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Mail, MapPin, Building2, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatRupiah, formatDateShort } from "@/lib/format";
import { waLink } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => navigate("/customers"));
  }, [id, navigate]);

  if (!c) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-40 w-full" /></div>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/customers")} className="-ml-2" data-testid="back-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> Kembali
      </Button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
              {(c.name || "?")[0].toUpperCase()}
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">{c.name}</h2>
              {c.company && <p className="text-sm text-muted-foreground">{c.company}</p>}
            </div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            {c.whatsapp && <div className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-muted-foreground" />{c.whatsapp}</div>}
            {c.email && <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" />{c.email}</div>}
            {c.address && <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />{c.address}</div>}
            {c.company && <div className="flex items-center gap-3"><Building2 className="h-4 w-4 text-muted-foreground" />{c.company}</div>}
          </div>
          {c.whatsapp && (
            <a href={waLink(c.whatsapp, `Halo ${c.name},`)} target="_blank" rel="noreferrer" className="mt-5 block">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="chat-wa-btn">
                <MessageCircle className="mr-2 h-4 w-4" /> Chat WhatsApp
              </Button>
            </a>
          )}
          {c.notes && <div className="mt-4 rounded-lg bg-secondary p-3 text-sm text-muted-foreground">{c.notes}</div>}
        </div>

        <div className="lg:col-span-2">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Invoice</p>
              <p className="mt-1 font-heading text-2xl font-bold">{c.invoices?.length || 0}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Nilai</p>
              <p className="mt-1 font-heading text-2xl font-bold">{formatRupiah(c.total_invoiced)}</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            {c.invoices?.length === 0 ? (
              <EmptyState icon={FileText} title="Belum ada transaksi" description="Customer ini belum memiliki invoice." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>No. Invoice</TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.invoices.map((inv) => (
                    <TableRow key={inv.id} className="cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <TableCell className="font-mono text-xs"><Link to={`/invoices/${inv.id}`} className="text-primary hover:underline">{inv.number}</Link></TableCell>
                      <TableCell>{formatDateShort(inv.date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatRupiah(inv.grand_total)}</TableCell>
                      <TableCell><StatusBadge status={inv.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
